import React, { useMemo } from 'react';
import CompactSelectPicker from './CompactSelectPicker';
import UserAvatar from './UserAvatar';

/**
 * Pick a team member — the assignee and owner pickers on a board task.
 *
 * Shared by the detail modal and the avatar on the card itself so the two can
 * never drift: the same options, the same ordering, the same "Me" row. A
 * picker that offers different people depending on where you opened it is the
 * kind of bug nobody reports, because each half looks right on its own.
 *
 * Every row carries the member's real avatar. That is not decoration: teams
 * routinely contain two people with the same first name, and a list of bare
 * display names makes them genuinely indistinguishable at the moment you are
 * choosing between them.
 */

/** The sentinel for "nobody". Empty string on purpose — the write path does
 *  `value || null`, so an empty sentinel collapses to a real `null` and an
 *  assignee actually clears. A non-empty sentinel like '__none__' would be
 *  stored verbatim as a user id that matches nobody. */
export const UNASSIGNED = '';

const AVATAR_SIZE = 18;

/**
 * On a board card the picker's trigger is styled down to nothing but the
 * avatar (see `.task-card-assignee-picker`), so the avatar there has to be
 * card-sized rather than row-sized.
 */

/**
 * You appear as "Me", never as your own name.
 *
 * You already know who you are, and "Me" is the row people are looking for
 * most often — on a long team it is findable at a glance instead of needing to
 * be read for. `searchText` keeps the real name searchable, so typing your own
 * name still lands on it.
 */
function optionForMember(member, currentUserId, avatarSize, avatarTone) {
  const isSelf = currentUserId != null && String(member.id) === String(currentUserId);
  const name = member.name || member.email || 'Unknown member';
  return {
    value: String(member.id),
    label: isSelf ? 'Me' : name,
    searchText: isSelf ? `Me ${name}` : name,
    leading: <UserAvatar userId={member.id} name={name} size={avatarSize} tone={avatarTone} />,
  };
}

export default function MemberSelect({
  value,
  onChange,
  members = [],
  currentUserId = null,
  emptyLabel = 'Unassigned',
  // Optional node for the "nobody" row. The board card passes an add-person
  // placeholder, because there the trigger is nothing BUT this node — with no
  // leading, an unassigned card's picker would have no visible target to
  // click.
  emptyLeading = null,
  className = '',
  avatarSize = AVATAR_SIZE,
  // 'pastel' on board cards and rows: the initials fallback in the person's
  // pastel with dark initials (UserAvatar), as on the Boards artboards.
  avatarTone = undefined,
  'aria-label': ariaLabel = 'Select member',
  onOpenChange,
}) {
  const selected = value == null ? UNASSIGNED : String(value);

  const options = useMemo(() => {
    const rows = [
      { value: UNASSIGNED, label: emptyLabel, searchText: emptyLabel, leading: emptyLeading },
    ];
    // "Me" first: it is the most-picked row on any board, and its position
    // must not depend on where the current user happens to sort by name.
    const sorted = [...members].sort((a, b) => {
      const aSelf = currentUserId != null && String(a.id) === String(currentUserId);
      const bSelf = currentUserId != null && String(b.id) === String(currentUserId);
      if (aSelf !== bSelf) return aSelf ? -1 : 1;
      return (a.name || a.email || '').localeCompare(b.name || b.email || '');
    });
    for (const m of sorted) {
      if (m?.id != null) rows.push(optionForMember(m, currentUserId, avatarSize, avatarTone));
    }

    // Someone can hold a task after leaving the team, and the member list is
    // fetched asynchronously. Without this row the picker would silently show
    // the first option instead of the real value, and simply opening it would
    // look like the task had been reassigned.
    if (selected !== UNASSIGNED && !rows.some((r) => r.value === selected)) {
      rows.push({
        value: selected,
        label: 'Unknown member',
        searchText: 'Unknown member',
        leading: <UserAvatar userId={selected} name="?" size={avatarSize} tone={avatarTone} />,
      });
    }
    return rows;
  }, [members, currentUserId, emptyLabel, emptyLeading, selected, avatarSize, avatarTone]);

  return (
    <CompactSelectPicker
      className={className}
      value={selected}
      // `|| null` and never undefined: the task is serialised straight into the
      // synced record's `data` map, and an undefined field disappears through
      // JSON — clearing it would work locally and never clear anywhere else.
      onChange={(v) => onChange(v || null)}
      options={options}
      // Always, not above a size threshold. A picker that grows a search box
      // once the team passes some invisible line teaches people it has no
      // search — they stop looking for it, and never find it on the boards
      // where they most need it. `searchText` means typing your own name still
      // matches your row even though it is labelled "Me".
      searchable
      searchPlaceholder="Search members…"
      onOpenChange={onOpenChange}
      aria-label={ariaLabel}
    />
  );
}
